/**
 * InboxPage (PHASE1 §7.2; groups: "Serien" plan) — the categorization inbox,
 * the app's centerpiece.
 *
 * One booked transaction at a time: tap a bucket, accept the suggestion, split,
 * or skip. Keyboard `1–9` assign the numbered buckets, `Enter` accepts the
 * suggestion, `s` skips, `u` undoes (LIFO). Touch: swipe right accepts the
 * suggestion, swipe left skips. When the current item belongs to a detected
 * group, a `GroupBanner` renders between the card and the grid; its checkbox
 * (hotkey `g`) governs whether the grid taps / `Enter` / `1–9` apply to the
 * whole group at once — the handlers themselves are unchanged, the
 * group-awareness lives entirely in `useInboxController`. All queue/undo/
 * optimistic logic lives there; this component is pure presentation + input
 * wiring.
 */
import { useEffect, useMemo, useState } from 'react';
import { SkipForward, Undo2 } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { StaleDataNotice } from '../components/ui/StaleDataNotice.tsx';
import { useInbox, useInboxController } from '../lib/hooks/useInbox.ts';
import { useHotkeys, type HotkeyMap } from '../lib/hooks/useHotkeys.ts';
import { useSwipe } from '../lib/hooks/useSwipe.ts';
import { ruleInitialFromGroup } from '../lib/hooks/useRules.ts';
import { InboxProgress } from './inbox/InboxProgress.tsx';
import { TransactionCard } from './inbox/TransactionCard.tsx';
import { GroupBanner } from './inbox/GroupBanner.tsx';
import { BucketGrid } from './inbox/BucketGrid.tsx';
import { SplitEditor } from './inbox/SplitEditor.tsx';
import { UndoToast } from './inbox/UndoToast.tsx';
import { RuleFormDialog } from './rules/RuleFormDialog.tsx';

export default function InboxPage() {
  const inbox = useInboxController();
  const {
    current,
    remaining,
    isLoading,
    isError,
    error,
    refetch,
    gridBuckets,
    currentGroup,
    groupApplyEnabled,
    setGroupApplyEnabled,
    ignoreGroup,
    liveMemberIds,
    lastGroupBucketId,
    assign,
    acceptSuggestion,
    split,
    skip,
    undo,
    canUndo,
    lastAction,
  } = inbox;

  const [splitOpen, setSplitOpen] = useState(false);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);

  // On phones the bucket grid extends past the viewport, so a tap far down the
  // grid loads the next item with its card off-screen above the fold. Jump back
  // up whenever the current item changes (covers assign/skip/undo and the final
  // „Alles kategorisiert“ state). matchMedia is optional-chained for jsdom.
  const currentId = current?.id ?? null;
  useEffect(() => {
    if (window.scrollY === 0) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [currentId]);

  // Same query key as the controller (shared cache entry) — tells us whether
  // in-memory data survived a failed refetch, so the error state is reserved
  // for error-with-no-data (§5.6 „zuletzt geladene Daten“).
  const hasData = useInbox().data !== undefined;

  // Prefilled from the group at the moment "Als Regel speichern" is clicked;
  // the bucket falls back to the last group action's bucket when the group
  // itself carries no suggestion.
  const ruleInitial = useMemo(() => {
    if (!currentGroup) return null;
    const bucketId = currentGroup.suggestion?.bucket_id ?? lastGroupBucketId ?? null;
    return ruleInitialFromGroup(currentGroup, bucketId);
  }, [currentGroup, lastGroupBucketId]);

  // Numeric hotkeys map onto the canonical grid order; `Enter`/`s`/`u` for the
  // suggestion/skip/undo actions — group-awareness is handled inside the
  // controller, so these need no changes. `g` toggles the group checkbox when
  // a group is detected for the current item. Disabled while a modal is open.
  const hotkeys = useMemo<HotkeyMap>(() => {
    const map: HotkeyMap = {
      Enter: acceptSuggestion,
      s: skip,
      u: undo,
    };
    for (let i = 0; i < 9; i += 1) {
      map[String(i + 1)] = () => {
        const bucket = gridBuckets[i];
        if (bucket) assign(bucket.id);
      };
    }
    if (currentGroup) {
      map.g = () => setGroupApplyEnabled(!groupApplyEnabled);
    }
    return map;
  }, [
    acceptSuggestion,
    skip,
    undo,
    assign,
    gridBuckets,
    currentGroup,
    groupApplyEnabled,
    setGroupApplyEnabled,
  ]);

  useHotkeys(hotkeys, current !== null && !splitOpen && !ruleDialogOpen);

  const swipe = useSwipe({
    onSwipeRight: () => {
      if (current?.suggestion) acceptSuggestion();
    },
    onSwipeLeft: () => {
      if (current) skip();
    },
  });

  return (
    <div className="flex flex-col">
      <PageHeader title="Eingang" subtitle="Buchungen kategorisieren" />

      <div className="w-full space-y-4 px-4 py-6 sm:px-6">
        <InboxProgress remaining={remaining} />

        {isLoading ? (
          <div className="py-2">
            <SkeletonList rows={4} />
          </div>
        ) : !current && isError && !(hasData && remaining === 0) ? (
          // Error only when there is nothing better to show: with cached items
          // the queue keeps rendering (data-first, §5.6), and a fully worked-off
          // queue still shows „Alles kategorisiert“.
          <ErrorState title="Eingang konnte nicht geladen werden" error={error} onRetry={refetch} />
        ) : !current ? (
          remaining > 0 ? (
            <div className="py-2">
              <SkeletonList rows={4} />
            </div>
          ) : (
            <EmptyState
              title="Alles kategorisiert"
              description="Es warten keine Buchungen mehr im Eingang."
              action={
                canUndo ? (
                  <Button variant="secondary" size="sm" onClick={undo}>
                    <Undo2 className="h-4 w-4" aria-hidden />
                    Letzte Aktion rückgängig
                  </Button>
                ) : undefined
              }
            />
          )
        ) : (
          <>
            {isError ? <StaleDataNotice onRetry={refetch} /> : null}

            <div
              onTouchStart={swipe.onTouchStart}
              onTouchMove={swipe.onTouchMove}
              onTouchEnd={swipe.onTouchEnd}
            >
              <TransactionCard item={current} />
            </div>

            {currentGroup ? (
              <GroupBanner
                group={currentGroup}
                applyEnabled={groupApplyEnabled}
                liveCount={liveMemberIds(currentGroup).length}
                onToggleApply={setGroupApplyEnabled}
                onIgnore={() => ignoreGroup(currentGroup.key)}
                onSaveRule={() => setRuleDialogOpen(true)}
              />
            ) : null}

            <BucketGrid
              buckets={gridBuckets}
              suggestion={current.suggestion}
              onAssign={assign}
              onAccept={acceptSuggestion}
              onSplit={() => setSplitOpen(true)}
            />

            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={skip}>
                <SkipForward className="h-4 w-4" aria-hidden />
                Überspringen
                <kbd className="rounded bg-slate-200 px-1 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                  s
                </kbd>
              </Button>
              <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo}>
                <Undo2 className="h-4 w-4" aria-hidden />
                Rückgängig
                <kbd className="rounded bg-slate-200 px-1 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                  u
                </kbd>
              </Button>
            </div>

            <SplitEditor
              open={splitOpen}
              onOpenChange={setSplitOpen}
              transaction={current}
              onConfirm={split}
            />

            {ruleInitial ? (
              <RuleFormDialog
                open={ruleDialogOpen}
                onOpenChange={setRuleDialogOpen}
                initial={ruleInitial}
              />
            ) : null}
          </>
        )}
      </div>

      <UndoToast action={lastAction} canUndo={canUndo} onUndo={undo} />
    </div>
  );
}
